import { useCallback, useState } from 'react'

import { Modal } from 'app/components/design-system-components'
import theme from 'app/match/applicationReportPDF/assets/theme'
import RentalHistoryForm from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm'
import type { ApplicationApplicant } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/sections/TenantsSection'
import type { ManualRentalHistoryEditData } from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/types'

// Define basic interface for the form data
interface RentalHistoryFormData {
  address: any
  propertyArrangement: string | number
  startDate: Date | string | null
  endDate: Date | string | null
  reasonForLeaving: string
  otherReason: string
  rate: string
  bond: string
  manager?: any
  shouldContactReference: boolean
  sendReferenceOnAdd: boolean
  tenants: any[]
  ledgerAttachments: any[]
  isUploading: boolean
}

interface AddAddressHistoryModalProps {
  onClose: () => void
  onSave?: (formData: RentalHistoryFormData) => void
  match?: any // Optional match prop for routing
  applicantGUID: string
  agencyGUID: string
  editingRentalHistory: ManualRentalHistoryEditData | null
  isEditMode: boolean
  isQLDForm22: boolean
  isRentalLedgerRequiredByAgencyPolicy?: boolean
  rentalHistoryForPolicy?: Record<string, unknown>[]
  applicants?: ApplicationApplicant[] // New prop for auto-fill functionality
  onManualRentalHistoryUpdate?: (
    data: Record<string, unknown>,
    action: string,
    guidID?: string,
  ) => void
}

const AddAddressHistoryModal = ({
  onClose,
  onSave,
  applicantGUID,
  agencyGUID,
  editingRentalHistory,
  isEditMode,
  isQLDForm22 = false,
  isRentalLedgerRequiredByAgencyPolicy = false,
  rentalHistoryForPolicy = [],
  applicants = [],
  onManualRentalHistoryUpdate,
}: AddAddressHistoryModalProps) => {
  // State to track form status for reactive UI
  const [isFormLoading, setIsFormLoading] = useState(false)
  const [canFormSubmit, setCanFormSubmit] = useState(false)

  // Critical: State to track current editing data with updated ledger attachments
  const [currentEditData, setCurrentEditData] =
    useState<ManualRentalHistoryEditData | null>(editingRentalHistory)

  // Form submit handler reference
  const [formSubmitHandler, setFormSubmitHandler] =
    useState<(() => void) | null>(null)

  // Handler for modal primary action (Save/Update button)
  const handlePrimaryAction = () => {
    if (formSubmitHandler) {
      formSubmitHandler()
    }
  }

  // Handler for form submission success
  const handleSubmit = async (formData: RentalHistoryFormData) => {
    try {
      if (onSave) {
        await onSave(formData)
      }
      onClose()
    } catch (error) {
      console.error('Failed to save rental history:', error)
      // Error handling is managed by the form component
    }
  }

  // Handler for form submission error
  const handleError = (error: string) => {
    console.error('Form validation/submission error:', error)
    // Could show toast notification here if needed
  }

  // Callback to handle form status changes
  const handleFormStatusChange = useCallback(
    (isLoading: boolean, canSubmit: boolean) => {
      setIsFormLoading(isLoading)
      setCanFormSubmit(canSubmit)
    },
    [],
  )

  // Callback to handle form submit setup
  const handleFormSubmitSetup = useCallback((submitHandler: () => void) => {
    setFormSubmitHandler(() => submitHandler)
  }, [])

  // Ledger callback handlers - Force form re-mount on changes
  const handleLedgerUpload = useCallback(
    (newLedgers: any[]) => {
      setCurrentEditData((prev) => {
        if (!prev) return prev

        const currentLedgers = prev.ledger || prev.ledgerAttachments || []
        const updatedEditData = {
          ...prev,
          ledger: [...currentLedgers, ...newLedgers],
          ledgerAttachments: [...currentLedgers, ...newLedgers],
        }

        // CRITICAL: Update parent component's manual rental history state
        if (onManualRentalHistoryUpdate && prev.guidID) {
          onManualRentalHistoryUpdate(updatedEditData, 'update', prev.guidID)
        }

        return updatedEditData
      })
    },
    [onManualRentalHistoryUpdate],
  )

  const handleLedgerDelete = useCallback(
    (guidID: string) => {
      setCurrentEditData((prev) => {
        if (!prev) return prev

        const currentLedgers = prev.ledger || prev.ledgerAttachments || []
        const filteredLedgers = currentLedgers.filter(
          (ledger: any) => ledger.guidID !== guidID,
        )
        const updatedEditData = {
          ...prev,
          ledger: filteredLedgers,
          ledgerAttachments: filteredLedgers,
        }

        // CRITICAL: Update parent component's manual rental history state
        if (onManualRentalHistoryUpdate && prev.guidID) {
          onManualRentalHistoryUpdate(updatedEditData, 'update', prev.guidID)
        }

        return updatedEditData
      })
    },
    [onManualRentalHistoryUpdate],
  )

  return (
    <Modal
      modalBody={
        <div style={{ maxWidth: theme.width[34] + 'px', width: '100%' }}>
          <RentalHistoryForm
            isEditMode={isEditMode}
            editData={currentEditData}
            onSuccess={handleSubmit}
            onError={handleError}
            isQLDForm22={isQLDForm22}
            isRentalLedgerRequiredByAgencyPolicy={
              isRentalLedgerRequiredByAgencyPolicy
            }
            rentalHistoryForPolicy={rentalHistoryForPolicy}
            editingRentalHistoryGUID={currentEditData?.guidID}
            isManualMode
            applicantGUID={applicantGUID}
            agencyGUID={agencyGUID}
            applicants={applicants}
            hideSubmitButton={true}
            onStatusChange={handleFormStatusChange}
            onSubmitReady={handleFormSubmitSetup}
            onLedgerUpload={handleLedgerUpload}
            onLedgerDelete={handleLedgerDelete}
          />
        </div>
      }
      modalHeading={isEditMode ? 'Edit address history' : 'Add address history'}
      primaryLabel={isEditMode ? 'Update' : 'Save'}
      secondaryLabel="Cancel"
      toggleModal={onClose}
      secondaryAction={onClose}
      primaryAction={handlePrimaryAction}
      primaryButtonLoading={isFormLoading}
      primaryButtonDisabled={!canFormSubmit}
      modalFooter={undefined}
    />
  )
}

export default AddAddressHistoryModal
